/**
 * Neighborhood Selection Module - Phase 1.2
 * Last-Minute Insertion Implementation
 *
 * Finds candidate aircraft that could be moved to make space
 * for last-minute insertions.
 *
 * Critical optimization: Size compatibility filter eliminates 80%+ of candidates
 * by only considering aircraft in spots where the target aircraft can physically fit.
 */

import { ParkingRequest, Spot, Placement, PlacementSegment } from "./types";
import { computeMoveability } from "./moveabilityScoring";

/**
 * Get spot IDs where aircraft is placed during target's time window
 * Handles both single-segment and multi-segment placements
 */
function getSpotsOccupiedDuringWindow(
  placement: { spotId: string; orientationId: string } | { segments: PlacementSegment[]; moveCount: number },
  targetStart: number,
  targetEnd: number
): string[] {
  if ("spotId" in placement) {
    // Single segment - always occupies this spot
    return [placement.spotId];
  }

  // Multi-segment - find segments that overlap target's time window
  const overlappingSpots: string[] = [];
  for (const segment of placement.segments) {
    // Check if segment overlaps with target window
    // Overlap if: segment.end > targetStart AND segment.start < targetEnd
    if (segment.endBucket > targetStart && segment.startBucket < targetEnd) {
      overlappingSpots.push(segment.spotId);
    }
  }
  return overlappingSpots;
}

/**
 * Configuration options for neighborhood selection
 */
export interface NeighborhoodOptions {
  /** Maximum number of candidates to return (default: 8) */
  maxNeighborSize?: number;
}

/**
 * Find candidate aircraft that could be moved to make space for target
 *
 * Selection process:
 * 1. Filter by temporal overlap (only aircraft present during target's dwell)
 * 2. Filter by size compatibility (only aircraft in spots where target can fit)
 * 3. Compute moveability score for each candidate
 * 4. Sort by moveability (cheapest to move first)
 * 5. Take top N candidates (default: 8)
 *
 * @param target New aircraft trying to fit
 * @param existingPlacements Current placement mapping
 * @param spots All available spots on ramp
 * @param allAircraft All aircraft in the schedule
 * @param options Selection configuration
 * @returns Array of candidate aircraft, sorted by moveability (easiest first)
 */
export function findRepairNeighborhood(
  target: ParkingRequest,
  existingPlacements: Placement,
  spots: Spot[],
  allAircraft: ParkingRequest[],
  options: NeighborhoodOptions = {}
): ParkingRequest[] {
  const { maxNeighborSize = 8 } = options;

  // Performance optimization: Create spot lookup map (O(1) instead of O(n))
  const spotMap = new Map(spots.map((s) => [s.id, s]));

  const candidates: Array<{ aircraft: ParkingRequest; moveability: number }> =
    [];

  for (const other of allAircraft) {
    // Skip the target aircraft itself
    if (other.id === target.id) {
      continue;
    }

    // Filter 1: Temporal overlap
    // Aircraft must be present during some portion of target's dwell
    // Overlap condition: other departs after target arrives AND other arrives before target departs
    const overlaps =
      other.departureBucket > target.arrivalBucket &&
      other.arrivalBucket < target.departureBucket;

    if (!overlaps) {
      continue;
    }

    // Filter 2: SIZE COMPATIBILITY - Critical optimization!
    // Only consider aircraft in spots where target can physically fit
    // This eliminates 80%+ of candidates immediately
    const otherPlacement = existingPlacements[other.id];
    if (!otherPlacement) {
      continue; // Aircraft not placed yet, skip
    }

    // Get all spots where 'other' is located during target's time window
    const occupiedSpotIds = getSpotsOccupiedDuringWindow(
      otherPlacement,
      target.arrivalBucket,
      target.departureBucket
    );

    if (occupiedSpotIds.length === 0) {
      continue; // No spots occupied during overlap (shouldn't happen if temporal filter worked)
    }

    // Check if target can fit in ANY of the spots this aircraft occupies
    const targetCanFitInAnySpot = occupiedSpotIds.some((spotId) => {
      const spot = spotMap.get(spotId);
      return spot && spot.allowedSizes.includes(target.sizeCategory);
    });

    if (!targetCanFitInAnySpot) {
      continue; // Skip - target can't use any of the spots this aircraft occupies
    }

    // Filter 3: Compute moveability
    const moveability = computeMoveability(other, spots);
    candidates.push({ aircraft: other, moveability });
  }

  // Sort by moveability (lower score = easier to move = higher priority)
  candidates.sort((a, b) => a.moveability - b.moveability);

  // Take top N candidates
  const topCandidates = candidates.slice(0, maxNeighborSize);

  // Return just the aircraft (strip moveability scores)
  return topCandidates.map((c) => c.aircraft);
}
